/**
 * Workspace Context Provider
 * Manages workspace state across the application
 *
 * @module contexts/WorkspaceContext
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { getUserWorkspaces } from "@/services/workspace";
import { setActiveWorkspaceId } from "@/services/activeWorkspace";
import { useAuth } from "./AuthContext";
import type { Workspace } from "@/types";
import { debug, info, warn, logError } from '../services/logger';
import { useTranslation } from "react-i18next";

// localStorage key for persisting selected workspace
const STORAGE_KEY = "selectedWorkspaceId";

// Custom event dispatched when workspace changes
export const WORKSPACE_CHANGED_EVENT = "workspaceChanged";

/**
 * Safely get item from localStorage with error handling
 */
const getStoredItem = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    warn('WorkspaceContext', 'localStorage.getItem failed');
    return null;
  }
};

/**
 * Safely set item in localStorage with error handling
 */
const setStoredItem = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    warn('WorkspaceContext', 'localStorage.setItem failed');
  }
};

/**
 * Dispatch workspace change event for data refresh
 */
const dispatchWorkspaceChangeEvent = (workspace: Workspace): void => {
  const event = new CustomEvent(WORKSPACE_CHANGED_EVENT, {
    detail: { workspaceId: workspace.id, workspaceName: workspace.name },
  });
  window.dispatchEvent(event);
  info('WorkspaceContext', 'Dispatched workspaceChanged event for', workspace.name);
};

/**
 * Workspace Context Value Type
 */
interface WorkspaceContextValue {
  workspace: Workspace | null;
  isLoading: boolean;
  error: Error | null;
  refreshWorkspace: () => Promise<void>;
  workspaces: Workspace[];
  switchWorkspace: (workspaceId: string) => Promise<void>;
  isSwitching: boolean;
}

// React Context for workspace state
const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

/**
 * Custom hook to access workspace context
 * Must be used within a WorkspaceProvider
 *
 * @throws Error if used outside of WorkspaceProvider
 */
export const useWorkspace = (): WorkspaceContextValue => {
  const context = useContext(WorkspaceContext);

  if (!context) {
    throw new Error("useWorkspace must be used within a WorkspaceProvider");
  }

  return context;
};

interface WorkspaceProviderProps {
  children: ReactNode;
}

/**
 * Workspace Provider Component
 * Fetches and provides workspace context to child components
 */
export const WorkspaceProvider: React.FC<WorkspaceProviderProps> = ({
  children,
}) => {
  // Read-only: the no-workspace screen needs the signed-in identity to name in
  // its copy, and Sign Out is the only action that can change the state that
  // screen is gated on.
  const { session, signOut } = useAuth();

  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const { t } = useTranslation();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSwitching, setIsSwitching] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Fetch workspace data from service
   */
  const fetchWorkspace = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Fetch all user workspaces first
      const allWorkspaces = await getUserWorkspaces();
      setWorkspaces(allWorkspaces);

      if (allWorkspaces.length === 0) {
        // No workspace found - user may need to create one
        info('WorkspaceContext', 'No workspace found for current user');
        setWorkspace(null);
        return;
      }

      // Check localStorage for previously selected workspace
      const storedWorkspaceId = getStoredItem(STORAGE_KEY);
      let selectedWorkspace: Workspace | null = null;

      if (storedWorkspaceId) {
        // Find the stored workspace in the list
        selectedWorkspace =
          allWorkspaces.find((ws) => ws.id === storedWorkspaceId) ?? null;
        if (selectedWorkspace) {
          info('WorkspaceContext', 'Restored workspace from localStorage', selectedWorkspace.name);
        } else {
          info('WorkspaceContext', 'Stored workspace not found, falling back to first workspace');
        }
      }

      // Fall back to first workspace if no valid stored selection
      if (!selectedWorkspace) {
        selectedWorkspace = allWorkspaces[0];
      }

      setWorkspace(selectedWorkspace);
      info('WorkspaceContext', 'Workspace loaded', selectedWorkspace.name);
    } catch (err) {
      logError('WorkspaceContext', 'Failed to fetch workspace', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      setWorkspace(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Manual refresh function exposed to consumers
   */
  const refreshWorkspace = useCallback(async () => {
    info('WorkspaceContext', 'Refreshing workspace...');
    await fetchWorkspace();
  }, [fetchWorkspace]);

  /**
   * Switch to a different workspace
   */
  const switchWorkspace = useCallback(
    async (workspaceId: string) => {
      const targetWorkspace = workspaces.find((ws) => ws.id === workspaceId);

      if (!targetWorkspace) {
        logError('WorkspaceContext', 'Workspace not found', undefined, { workspaceId });
        return;
      }

      if (workspace?.id === workspaceId) {
        debug('WorkspaceContext', 'Already on workspace', targetWorkspace.name);
        return;
      }

      info('WorkspaceContext', 'Switching to workspace', targetWorkspace.name);
      setIsSwitching(true);

      try {
        // Update workspace state
        setWorkspace(targetWorkspace);
        // Persist to localStorage
        setStoredItem(STORAGE_KEY, workspaceId);
        // Dispatch event for data refresh
        dispatchWorkspaceChangeEvent(targetWorkspace);
        info('WorkspaceContext', 'Workspace switched to', targetWorkspace.name);
      } catch (err) {
        logError('WorkspaceContext', 'Failed to switch workspace', err);
      } finally {
        setIsSwitching(false);
      }
    },
    [workspaces, workspace?.id]
  );

  // Fetch workspace on mount
  useEffect(() => {
    info('WorkspaceContext', 'Initializing...');
    fetchWorkspace();
  }, [fetchWorkspace]);

  // One publisher for four setWorkspace call sites: keyed on the id, so what the
  // AI proxy is told always matches the workspace the user is looking at.
  useEffect(() => {
    setActiveWorkspaceId(workspace?.id ?? null);
  }, [workspace?.id]);

  // Context value - memoized to prevent unnecessary re-renders
  const contextValue = useMemo<WorkspaceContextValue>(
    () => ({
      workspace,
      isLoading,
      error,
      refreshWorkspace,
      workspaces,
      switchWorkspace,
      isSwitching,
    }),
    [
      workspace,
      isLoading,
      error,
      refreshWorkspace,
      workspaces,
      switchWorkspace,
      isSwitching,
    ]
  );

  // Show loading spinner during initial fetch
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto mb-4" />
          <p className="text-gray-600 text-sm">{t("workspace.loading")}</p>
        </div>
      </div>
    );
  }

  // Show error state if fetch failed
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center max-w-md p-6 bg-white rounded-lg shadow-sm">
          <div className="text-red-600 mb-4">
            <svg
              className="h-12 w-12 mx-auto"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            {t("workspace.loadFailed")}
          </h2>
          <p className="text-gray-600 text-sm mb-4">
            {error.message || t("workspace.unexpectedError")}
          </p>
          <button
            onClick={refreshWorkspace}
            className="px-4 py-2 bg-primary-600 text-ink-950 rounded-md hover:bg-primary-700 transition-colors"
          >
            {t("workspace.tryAgain")}
          </button>
        </div>
      </div>
    );
  }

  // No workspace resolved.
  //
  // Only an ALREADY SIGNED-IN user reaches this — App.tsx redirects to
  // /auth/signin before mounting this provider when there is no session. So the
  // call to action must be Sign Out, never Sign In: /auth/signin sees the live
  // session and bounces straight back here (App.tsx isAuthPage -> Navigate to
  // /campaigns), and because this returns instead of {children}, Layout never
  // mounts and the app's only Logout button is unreachable. The previous
  // "Sign In" / "Create Account" pair left no way out but clearing localStorage
  // by hand.
  //
  // Signing out is the only action that changes the state this screen is gated
  // on. Everything else is the administrator's job, which is what the copy says.
  if (!workspace) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center max-w-md p-6 bg-white rounded-lg shadow-sm">
          <div className="text-ink-900 mb-4">
            <svg
              className="h-12 w-12 mx-auto"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            {t("workspace.noneAvailable")}
          </h2>
          <p className="text-gray-600 text-sm mb-4">
            {session?.user?.email
              ? t("workspace.noAccessEmail", { email: session.user.email })
              : t("workspace.noAccess")}{" "}
            {t("workspace.askAdmin")}
          </p>
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => void refreshWorkspace()}
              className="block w-full px-4 py-2 bg-primary-600 text-ink-950 text-center rounded-md hover:bg-primary-700 transition-colors"
            >
              {t("workspace.tryAgain")}
            </button>
            <button
              type="button"
              onClick={() => void signOut()}
              className="block w-full px-4 py-2 border border-gray-300 text-gray-700 text-center rounded-md hover:bg-gray-50 transition-colors"
            >
              {t("workspace.signOut")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render children with context
  return (
    <WorkspaceContext.Provider value={contextValue}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export default WorkspaceContext;
